import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Payement } from '../model/model.payement';
import { Page } from '../model/model.page';
import { environment } from 'src/environments/environment';
import { DownloadPdfService } from './download-pdf.service';

@Injectable({
  providedIn: 'root'
})
export class PayementService {
  basePath = environment.BasePath + '/survey';

  constructor(
    private httpClient: HttpClient,
    private downloadPdfService: DownloadPdfService
  ) {}

  // Fonction utilitaire pour nettoyer les dates et assurer le format correct pour le backend
  private cleanPayementDate(payement: Payement): Payement {
    const cleanPayement = { ...payement };

    if (cleanPayement.datePay) {
      let dateTime: Date;

      // Convertir en string si ce n'est pas déjà le cas
      let dateStr = cleanPayement.datePay.toString();

      try {
        if (dateStr.includes('T')) {
          // C'est déjà une string ISO, l'utiliser directement
          dateTime = new Date(dateStr);
        } else if (dateStr.includes('/')) {
          // Format DD/MM/YYYY
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Les mois sont 0-based en JS
            const year = parseInt(parts[2], 10);
            dateTime = new Date(year, month, day);
            // Ajouter l'heure actuelle
            const now = new Date();
            dateTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
          } else {
            dateTime = new Date();
          }
        } else {
          // Essayer de parser la date
          dateTime = new Date(dateStr);
          if (isNaN(dateTime.getTime())) {
            dateTime = new Date();
          }
        }

        // Assurer qu'on a une heure valide
        if (dateTime.getHours() === 0 && dateTime.getMinutes() === 0 && dateTime.getSeconds() === 0) {
          const now = new Date();
          dateTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
        }

        // Convertir en ISO string avec le bon timezone
        cleanPayement.datePay = dateTime.toISOString();
      } catch (e) {
        console.error('Erreur lors de la conversion de date:', e);
        // En cas d'erreur, utiliser la date/heure actuelle
        dateTime = new Date();
        cleanPayement.datePay = dateTime.toISOString();
      }
    }

    return cleanPayement;
  }

  getPaginatedPayements(
    idEtab: number,
    page: number,
    size: number,
    sujetDon?: string,
    remarque?: string,
    mantant?: string,
    datePay?: string,
    nomKafil?: string,
    paymentReference?: string, // Already updated
    personne?: string,
    typePaiement?: string
  ): Observable<Page<Payement>> {
    if (!idEtab) {
      throw new Error('idEtab is required for paginated request');
    }
    let url = `${this.basePath}/payement/paginated?page=${page}&size=${size}`;
    const params: any = {};
    if (sujetDon) params.sujetDon = sujetDon;
    if (remarque) params.remarque = remarque;
    if (mantant) params.mantant = mantant;
    if (datePay) params.datePay = datePay;
    if (nomKafil) params.nomKafil = nomKafil;
    if (paymentReference) params.paymentReference = paymentReference;
    if (personne) params.personne = personne;
    if (typePaiement) params.typePaiement = typePaiement;

    return this.httpClient.get<Page<Payement>>(url, {
      headers: { 'idEtab': idEtab.toString() },
      params
    }).pipe(
      catchError((error) => {
        console.error('Error in getPaginatedPayements:', error);
        return throwError(error);
      })
    );
  }

  getPaginatedPayementsByInscrit(
    inscritId: number,
    page: number,
    size: number,
    sujetDon?: string,
    remarque?: string,
    mantant?: string,
    datePay?: string,
    personne?: string,
    nomKafil?: string,
    idPay?: string,
    typePaiement?: string // Ajout du paramètre typePaiement
  ): Observable<{
    content: Payement[];
    pageable: any;
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    totalMontant: number;
  }> {
    if (!inscritId) throw new Error('inscritId is required');

    const etablissementRaw = sessionStorage.getItem('etablissement');
    let etablissement: any = {};
    try {
      etablissement = etablissementRaw ? JSON.parse(etablissementRaw) : {};
    } catch (e) {
      console.error('Erreur lors du parsing de sessionStorage.etablissement:', e);
    }
    const idEtab = etablissement.id;

    let url = `${this.basePath}/payement/inscrit/${inscritId}/paginated?page=${page}&size=${size}`;
    const params: any = {};
    if (sujetDon) params.sujetDon = sujetDon;
    if (remarque) params.remarque = remarque;
    if (mantant) params.mantant = mantant;
    if (datePay) params.datePay = datePay;
    if (personne) params.personne = personne;
    if (nomKafil) params.nomKafil = nomKafil;
    if (idPay) params.idPay = idPay;
    if (typePaiement) params.typePaiement = typePaiement; // Ajout du paramètre typePaiement

    return this.httpClient.get<any>(url, {
      headers: { 'idEtab': idEtab.toString() },
      params
    }).pipe(
      map(response => ({
        content: response.content || [],
        pageable: {
          offset: (response.number || page) * (response.size || size),
          pageNumber: response.number || page,
          pageSize: response.size || size,
          paged: true,
          unpaged: false
        },
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
        size: response.size || size,
        number: response.number || page,
        numberOfElements: response.numberOfElements || 0,
        first: response.first || false,
        last: response.last || false,
        empty: response.empty || true,
        totalMontant: response.totalMontant || 0
      })),
      catchError((error) => {
        console.error('Error in getPaginatedPayementsByInscrit:', error);
        return throwError(error);
      })
    );
  }

  getAllTransaction(dated: string, datef: string, mnt: string, type: string, nb: string, mois: string, date: string) {
    const uri = `/print/transaction/${dated}/${datef}/${mnt}/${type}/${nb}/${mois}/${date}`;
    const httpOptions = {
      headers: new HttpHeaders({
        dated: dated,
        datef: datef,
        mnt: mnt,
        type: type,
        nb: nb,
        content: mois,
        date: date
      })
    };
    this.downloadPdfService.downloadPDF(this.basePath + uri, httpOptions);
  }

  getTotalAmount(
    idEtab: number,
    sujetDon?: string,
    remarque?: string,
    mantant?: string,
    datePay?: string,
    typePaiement?: string,
    nomKafil?: string,
    paymentReference?: string,
    personne?: string
  ): Observable<number> {
    if (!idEtab) {
      throw new Error('idEtab is required for total amount request');
    }
    let url = `${this.basePath}/payement/total`;
    const params: any = {};
    if (sujetDon) params.sujetDon = sujetDon;
    if (remarque) params.remarque = remarque;
    if (mantant) params.mantant = mantant;
    if (datePay) params.datePay = datePay;
    if (typePaiement) params.typePaiement = typePaiement;
    if (nomKafil) params.nomKafil = nomKafil;
    if (paymentReference) params.paymentReference = paymentReference;
    if (personne) params.personne = personne;

    return this.httpClient.get<number>(url, {
      headers: { 'idEtab': idEtab.toString() },
      params
    }).pipe(
            catchError((error) => {
        console.error('Error in getTotalAmount:', error);
                return throwError(error);
            })
        );
    }

  getPayements(motCle: string): Observable<any> {
    return this.httpClient.get(`${this.basePath}/cherchePayement?nom=${motCle}`).pipe(
      catchError((error) => {
        console.error('Error in getPayements:', error);
        return throwError(error);
      })
    );
  }

  getAllPayements(motCle: string): Observable<any> {
    return this.httpClient.get(`${this.basePath}/chercheAllPayement?nom=${motCle}`).pipe(
      catchError((error) => {
        console.error('Error in getAllPayements:', error);
        return throwError(error);
      })
    );
  }

  findAllPayements(): Observable<any> {
    return this.httpClient.get(`${this.basePath}/payement`).pipe(
      catchError((error) => {
        console.error('Error in findAllPayements:', error);
        return throwError(error);
      })
    );
  }

  getAllPayementsWithoutArgs(): Observable<any> {
    return this.httpClient.get(`${this.basePath}/chercheAllPayementWA`).pipe(
      catchError((error) => {
        console.error('Error in getAllPayementsWithoutArgs:', error);
        return throwError(error);
      })
    );
  }

  getEntree(motCle: string): Observable<any> {
    return this.httpClient.get(`${this.basePath}/chercheEntree?nom=${motCle}`).pipe(
      catchError((error) => {
        console.error('Error in getEntree:', error);
        return throwError(error);
      })
    );
  }

  getPayementByIdInscrit(id: number, motCle: string): Observable<any> {
    return this.httpClient.get(`${this.basePath}/cherchePayementsPerParInscrit/${id}?nom=${motCle}`).pipe(
      catchError((error) => {
        console.error('Error in getPayementByIdInscrit:', error);
        return throwError(error);
      })
    );
  }

  getPayementByIdPersonnel(id: number, motCle: string): Observable<any> {
    return this.httpClient.get(`${this.basePath}/chercherPayementsPerParPersonnel/${id}?nom=${motCle}`).pipe(
      catchError((error) => {
        console.error('Error in getPayementByIdPersonnel:', error);
        return throwError(error);
      })
    );
  }

  getPayementById(id: number): Observable<any> {
    return this.httpClient.get(`${this.basePath}/payement/${id}`).pipe(
      catchError((error) => {
        console.error('Error in getPayementById:', error);
        return throwError(error);
      })
    );
  }

  deletePayement(id: number): Observable<any> {
    const etablissementRaw = sessionStorage.getItem('etablissement');
    let idEtab = '';
    try {
      const etablissement = etablissementRaw ? JSON.parse(etablissementRaw) : {};
      idEtab = etablissement.id?.toString() || '';
    } catch (e) {
      console.error('Erreur lors du parsing de sessionStorage.etablissement:', e);
    }
    return this.httpClient.delete(`${this.basePath}/payement/${id}`, {
      headers: { 'idEtab': idEtab },
      responseType: 'text'
    }).pipe(
      catchError((error) => {
        console.error('Error in deletePayement:', error);
        return throwError(error);
      })
    );
  }

  editPayement(payement: Payement): Observable<any> {
    console.log('=== PAYEMENT SERVICE EDIT ===');
    console.log('Payement reçu:', payement);
    console.log('datePay original:', payement.datePay);

    const cleanPayement = this.cleanPayementDate(payement);

    console.log('Payement nettoyé:', cleanPayement);
    console.log('datePay final:', cleanPayement.datePay);
    console.log('=== FIN PAYEMENT SERVICE EDIT ===');

    const etablissementRaw = sessionStorage.getItem('etablissement');
    let idEtab = '';
    try {
      const etablissement = etablissementRaw ? JSON.parse(etablissementRaw) : {};
      idEtab = etablissement.id?.toString() || '';
    } catch (e) {
      console.error('Erreur lors du parsing de sessionStorage.etablissement:', e);
    }
    return this.httpClient.put(`${this.basePath}/payement/${payement.idPay}`, cleanPayement, {
      headers: { 'idEtab': idEtab }
    }).pipe(
      catchError((error) => {
        console.error('Error in editPayement:', error);
        return throwError(error);
      })
    );
  }

  savePayement(payement: Payement): Observable<any> {
    console.log('=== PAYEMENT SERVICE SAVE ===');
    console.log('Payement reçu:', payement);
    console.log('datePay original:', payement.datePay);

    const cleanPayement = this.cleanPayementDate(payement);

    console.log('Payement nettoyé:', cleanPayement);
    console.log('datePay final:', cleanPayement.datePay);
    console.log('=== FIN PAYEMENT SERVICE SAVE ===');

    const etablissementRaw = sessionStorage.getItem('etablissement');
    let idEtab = '';
    try {
      const etablissement = etablissementRaw ? JSON.parse(etablissementRaw) : {};
      idEtab = etablissement.id?.toString() || '';
    } catch (e) {
      console.error('Erreur lors du parsing de sessionStorage.etablissement:', e);
    }
    return this.httpClient.post(`${this.basePath}/payement`, cleanPayement, {
      headers: { 'idEtab': idEtab }
    }).pipe(
      catchError((error) => {
        console.error('Error in savePayement:', error);
        return throwError(error);
      })
    );
  }

  getPaymentReference(): Observable<any> {
    return this.httpClient.get(`${this.basePath}/payment/reference`).pipe(
      catchError((error) => {
        console.error('Error in getPaymentReference:', error);
        return throwError(error);
      })
    );
  }
}
